import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  MessageSquare,
  Send,
  RefreshCw,
  Clock,
  User,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Managed Client Messages Page - Phase 7
 * 
 * View and send messages to specialist.
 * Client-visible messages only.
 */
const ManagedClientMessagesPage = () => {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/managed/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError(err.response?.data?.detail || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMessages();
  }, [token]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/portal/managed/messages`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] max-h-[700px]" data-testid="managed-client-messages">
      {/* Header */}
      <div className="flex items-center justify-between pb-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Messages</h1>
          <p className="text-slate-500 mt-1">Communicate with your specialist</p>
        </div>
        <Button onClick={fetchMessages} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Messages Container */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
        {/* Messages List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length > 0 ? (
            messages.map((message, index) => {
              const isOwn = message.sender_user_id === user?.user_id;
              return (
                <div
                  key={message.message_id || index}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
                    {!isOwn && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <User size={12} className="text-emerald-600" />
                        </div>
                        <span className="text-xs text-slate-500 font-medium">Your Specialist</span>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        isOwn
                          ? 'bg-emerald-600 text-white rounded-br-sm'
                          : 'bg-slate-100 text-stone-900 rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                    </div>
                    <div className={`flex items-center gap-1 mt-1 text-xs text-slate-400 ${isOwn ? 'justify-end' : ''}`}>
                      <Clock size={10} />
                      <span>{formatTime(message.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <MessageSquare size={48} className="text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No Messages Yet</h3>
              <p className="text-slate-500 max-w-sm">
                Send a message to your specialist below. They typically respond within 1 business day.
              </p>
            </div>
          )}
        </div>

        {/* Message Input */}
        <form onSubmit={handleSendMessage} className="border-t border-slate-200 p-4">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1"
              disabled={sending}
            />
            <Button 
              type="submit" 
              disabled={!newMessage.trim() || sending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Send size={18} className={sending ? 'animate-pulse' : ''} />
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Messages are typically responded to within 1 business day.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ManagedClientMessagesPage;
